import type { IStore } from "~/state/store";
import type WasmStoreMapping from "../mapping";
import Wasm from "../wasm";
import { TWasmId } from "~/interfaces";
import { updateMarker } from "~/state/map-content/state";
import { openPanoramaAt } from "~/util/providers/pano";
import { getPOI } from "~/route/utils";
import Scene from "~/scene";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";

let dragDeltaX = 0;
let dragDeltaY = 0;
let routeDNDMarker: WasmStoreMarker = null;

export function onDragStart(wasmId: TWasmId, event: PointerEvent, store: IStore, mapping: WasmStoreMapping, wasm: Wasm) {
	const storeId = mapping.getWasmToStoreId(wasmId);
	const { markers, geometries } = store.getState().mapContent;
	const draggedMarker = markers.find(marker => marker.id === storeId);
	const draggedGeometry = geometries.find(geometry => geometry.id === storeId);

	// obecne tahani markeru jen tim, ze ma draggable = true
	if (draggedMarker?.draggable) {
		// spocitame si rozdil anchoru a kliku
		const draggedMarkerPosition = wasm.module.GeoMath.wgsToMercator({
			x: draggedMarker.lon,
			y: draggedMarker.lat,
		});
		const anchor = wasm.mapControl.mapPosToScreenPos(draggedMarkerPosition);

		dragDeltaX = event.clientX - anchor.x;
		dragDeltaY = event.clientY - anchor.y;

		store.dispatch(updateMarker({
			id: draggedMarker.id,
			data: {
				lastChange: draggedMarker.lastChange + 1,
				bringToTheFront: true,
			},
		}));
	} else {
		dragDeltaX = 0;
		dragDeltaY = 0;
	}

	// tazeni z geometrie rucniho mereni
	if (draggedGeometry?.type === "measure-line") {
		const wgs = wasm.coordsFromScreenPosition(event.clientX, event.clientY);
		const distanceMeter = Mapy.getComponent("distanceMeter").getMain();
		const items = distanceMeter?.getItems() ?? [];
		const index = draggedGeometry.customData?.index ?? 0;
		const item = items.length > index ? items[index] : null;
		const newItem = distanceMeter?.add([wgs.x, wgs.y], draggedGeometry.customData?.index ?? 0);

		if (item) {
			item._newPoint = newItem;
			item._owner.allowHover = false;
			item._wasEnabled = distanceMeter?.interactivePoint.enabled;
			distanceMeter.interactivePoint.hide(true);
		}

		return newItem?._wasmMarker.id ?? 0;
	// vytazeni z geometrie routovace
	} else if (draggedGeometry?.type === "custom" && draggedGeometry.customData?.route && draggedGeometry.customData.route.routeIndex === Mapy.CML.state.route.index) {
		routeDNDMarker?.destroy();

		const wgs = wasm.coordsFromScreenPosition(event.clientX, event.clientY);

		routeDNDMarker = new WasmStoreMarker({
			draggable: true,
			type: "little",
			lon: wgs.x,
			lat: wgs.y,
			customData: {
				order: draggedGeometry.customData.order,
				route: {
					newPoint: true,
				},
			},
		});

		return routeDNDMarker.id;
	}

	return draggedMarker?.draggable ? draggedMarker.id : 0;
}

export function onDragMove(id: number, event: PointerEvent, store: IStore, wasm: Wasm) {
	const { markers } = store.getState().mapContent;
	const draggedMarker = markers.find(marker => marker.id === id);

	if (draggedMarker?.draggable) {
		// obecna cast pro cokoliv, co ma draggable marker bez ohledu na to, komu ten marker patri
		const pos = wasm.mapControl.screenPosToMapPos({
			x: event.clientX - dragDeltaX,
			y: event.clientY - dragDeltaY,
		});
		const wgs = wasm.module.GeoMath.mercatorToWGS(pos);

		store.dispatch(updateMarker({
			id: draggedMarker.id,
			data: {
				lon: wgs.x,
				lat: wgs.y,
				lastChange: draggedMarker.lastChange + 1,
			},
		}));

		// TODO: az se jednoho dne presune funkcnost z interniho stavu komponenty do store, jeji cast tady se musi smazat, ale pro ted je tohle nejrychlejsi cesta dat z mista, kde se zmena stala, do mista, kde nas zajima
		// vlastni body - tazeni markeru
		if (Mapy.getComponent("userMarks").isActive() && draggedMarker.type === "user-marks") {
			const items = Mapy.getComponent("userMarks").getMain()?.getItems() || [];

			for (let index = 0; index < items.length; index++) {
				if (index === (draggedMarker.customData?.order ?? 0) - 1) {
					items[index].dragMove(wgs.x, wgs.y);
					break;
				}
			}
		} else if (Mapy.getComponent("distanceMeter").isActive() && (draggedMarker.type === "measure-points" || draggedMarker.type === "measure-start")) {
			const distanceMeter = Mapy.getComponent("distanceMeter").getMain();
			const items = distanceMeter?.getItems() ?? [];
			const index = draggedMarker.customData?.index ?? 0;
			const item = items.length > index ? items[index] : null;

			if (item && item._newPoint) {
				// upravime koncovy bod pred nami
				if (item._newPoint.prev) {
					item._newPoint.prev.endCoords = wgs;
				}

				// aktualizace dat
				item._newPoint.startCoords = wgs;

				// update - area, distance, posledni radek
				distanceMeter?.redraw({
					noFull: true,
				});
			}
		}
	}
}

export function onDragEnd(id: number, event: PointerEvent, store: IStore, wasm: Wasm) {
	const { markers } = store.getState().mapContent;
	const draggedMarker = markers.find(marker => marker.id === id);
	const pos = wasm.mapControl.screenPosToMapPos({
		x: event.clientX - dragDeltaX,
		y: event.clientY - dragDeltaY,
	});
	const wgs = wasm.module.GeoMath.mercatorToWGS(pos);

	// TODO: az se jednoho dne presune funkcnost z interniho stavu komponenty do store, jeji cast tady se musi smazat, ale pro ted je tohle nejrychlejsi cesta dat z mista, kde se zmena stala, do mista, kde nas zajima
	// vlastni body - tazeni markeru
	if (Mapy.getComponent("userMarks").isActive() && draggedMarker.type === "user-marks") {
		const items = Mapy.getComponent("userMarks").getMain()?.getItems() || [];

		for (let index = 0; index < items.length; index++) {
			if (index === (draggedMarker.customData?.order ?? 0) - 1) {
				items[index].dragStop();
				break;
			}
		}
	// marker oko panoramy
	} else if (draggedMarker.type === "pano") {
		const zoom = store.getState().map.zoom;
		const selYear = Mapy.getComponent("pano").getYear();

		openPanoramaAt({
			lon: wgs.x,
			lat: wgs.y,
			zoom,
			selYear,
		}).then(() => {
			Mapy.getComponent("history").save();
		}, () => {});
	// marker routovace
	} else if (Mapy.getComponent("route").isActive() && draggedMarker?.customData?.route) {
		// nacitame -> nebudeme menit polohu
		if (Mapy.CML.state.route.request) {
			return;
		}

		const coords = Scene.coordsFromWGS84(wgs.x, wgs.y);

		Mapy.CML.state = {
			route: {
				loading: true,
			},
		};

		getPOI(coords).then(data => {
			const items: Array<any> = Mapy.CML.state.route.items;

			if (!items[draggedMarker.customData.order - 1]) {
				return;
			}

			// tahame nas fake bod, ktery vznikl vytazenim z geometrie
			if (draggedMarker.customData.route.newPoint) {
				routeDNDMarker?.destroy();
				routeDNDMarker = null;
				items.splice(draggedMarker.customData.order, 0, {
					usertitle: "",
					favoriteId: "",
					...data,
				});
			// tahame existujici bod routy
			} else {
				Object.assign(items[draggedMarker.customData.order - 1], {
					usertitle: "",
					favoriteId: "",
				}, data);
			}
			Mapy.CML.state = {
				route: {
					items,
					loading: false,
				},
			};
			Mapy.getComponent("history").save();
		}, () => {});
	}
}
